(() => {
  'use strict';
  if (window.__mascoloriCartSnapshot) return;
  window.__mascoloriCartSnapshot = true;
  const root = window.Shopify?.routes?.root || '/';
  const originalFetch = window.fetch.bind(window);
  let timer, inFlight = false, pending = false, lastSignature = '', readinessRetries = 0;
  const consent = () => window.Shopify?.customerPrivacy?.marketingAllowed?.() === true;
  const absolute = value => value ? new URL(value, location.origin).href : '';
  function build(cart) {
    let supported = Array.isArray(cart.items) && cart.items.length > 0 && cart.items.length <= 50;
    const variants = new Map();
    const lines = (cart.items || []).filter(item => item.quantity > 0).map(item => {
      const properties = item.properties || {};
      if (item.selling_plan_allocation || Object.keys(properties).some(key => properties[key] && !['Old Price', 'Discount'].includes(key))) supported = false;
      const id = String(item.variant_id);
      if (!/^\d+$/.test(id) || !Number.isInteger(item.quantity) || item.quantity < 1) supported = false;
      variants.set(id, (variants.get(id) || 0) + item.quantity);
      const image = absolute(item.featured_image?.url || item.image);
      if (!image) supported = false;
      return {
        title: item.product_title || item.title,
        variant_title: item.variant_title || '',
        variant_id: id,
        quantity: item.quantity,
        price: item.final_price / 100,
        line_price: item.final_line_price / 100,
        url: absolute(item.url),
        product: { images: [{ src: image }] }
      };
    });
    const path = [...variants].map(([id, quantity]) => `${id}:${quantity}`).join(',');
    const cartUrl = supported ? `${location.origin}${root}cart/${path}?storefront=true` : '';
    return {
      schema_version: 1,
      captured_at: new Date().toISOString(),
      restore_supported: supported,
      item_count: cart.item_count,
      presentment_currency: cart.currency,
      cart_url: cartUrl,
      line_items: lines,
      total_price: cart.total_price / 100,
      locale: document.documentElement.lang || 'nl'
    };
  }
  function confirmed(promise, label) {
    let deadline;
    return Promise.race([Promise.resolve(promise), new Promise((_, reject) => {
      deadline = setTimeout(() => reject(new Error(label + ': no acknowledgement within 15 seconds')), 15000);
    })]).finally(() => clearTimeout(deadline));
  }
  async function snapshot() {
    if (!consent()) return;
    if (!window.klaviyo?.track || !window.klaviyo?.identify || !window.klaviyo?.isIdentified) {
      if (++readinessRetries <= 20) { clearTimeout(timer); timer = setTimeout(snapshot, 1500); }
      return;
    }
    readinessRetries = 0;
    if (inFlight) { pending = true; return; }
    inFlight = true;
    try {
      const identified = await confirmed(window.klaviyo.isIdentified(), 'Identity check');
      if (identified !== true) {
        console.info('Mascolori cart snapshot: visitor is not identified; no profile update or event sent');
        return;
      }
      console.info('Mascolori cart snapshot: identified visitor confirmed');
      const response = await originalFetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      if (!consent()) return;
      const payload = build(cart);
      const signature = JSON.stringify([payload.line_items, payload.presentment_currency, payload.total_price, payload.restore_supported]);
      if (signature === lastSignature) return;
      console.info('Mascolori cart snapshot: captured ' + payload.item_count + ' items; sending to Klaviyo');
      await confirmed(window.klaviyo.identify({ MascoloriCart: payload, MascoloriCartReady: payload.restore_supported }), 'Profile update');
      console.info('Mascolori cart snapshot: profile update completed; dispatching event');
      if (!consent()) return;
      const accepted = await confirmed(window.klaviyo.track('Mascolori Cart Snapshot', {
        '$event_id': `mc-${crypto.randomUUID()}`,
        '$value': payload.total_price,
        '$currency': payload.presentment_currency,
        'Customer Locale': payload.locale,
        'Cart URL': payload.cart_url,
        'Items': payload.line_items,
        'Item Count': payload.item_count,
        RestoreSupported: payload.restore_supported,
        ItemCount: payload.item_count,
        extra: payload
      }), 'Event delivery');
      if (accepted === false) throw new Error('Klaviyo did not acknowledge the event');
      console.info('Mascolori cart snapshot: event acknowledged by Klaviyo');
      lastSignature = signature;
    } catch (error) {
      console.warn('Mascolori cart snapshot could not be sent:', error?.message || 'Unknown error');
    } finally {
      inFlight = false;
      if (pending) { pending = false; schedule(); }
    }
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(snapshot, 600); }
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver(list => {
      if (list.getEntries().some(entry => {
        try { const url = new URL(entry.name); return url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname); }
        catch (_) { return false; }
      })) schedule();
    });
    observer.observe({ type: 'resource', buffered: false });
  }
  document.addEventListener('visitorConsentCollected', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('load', schedule);
  window.addEventListener('klaviyoForms', event => {
    if (event.detail?.type === 'submit') schedule();
  });
  if (window.Shopify?.customerPrivacy) schedule();
  else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.2' }], error => { if (!error) schedule(); });
})();
